import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Translate as TranslateIcon } from '@mui/icons-material';
import MyDialog from '../shared/Dialog';
import { t } from '../dashboard/coach/coachTokens';
import TranslationPanel from './TranslationPanel';
import type { TranslationPanelProps } from './TranslationPanel';

/**
 * The one-line drop-in: a button that opens {@link TranslationPanel} in the shared dialog.
 *
 * A dialog rather than inline, for a reason that gets worse with every language: the panel is
 * (fields x locales) inputs — 6 for an exercise today, 30 once ten locales are enabled. Inline,
 * that pushes the rest of the form off the screen as soon as the project succeeds. The host form
 * keeps one button of fixed size whatever the locale count becomes.
 *
 * Hosts that genuinely want it inline import `TranslationPanel` directly; this wrapper adds
 * nothing but the dialog.
 */
export interface TranslationDialogButtonProps extends TranslationPanelProps {
    /** Button label. Defaults to the generic Hebrew wording. */
    label?: string;
    /** Dialog title. Defaults to the button label. */
    dialogTitle?: string;
}

const TranslationDialogButton = ({
    label = 'תרגומים',
    dialogTitle,
    onSaved,
    ...panelProps
}: TranslationDialogButtonProps) => {
    const [open, setOpen] = useState(false);
    const [dirty, setDirty] = useState(false);

    // A dialog close with unsaved edits would silently drop them; the panel saves per locale, so
    // confirm rather than auto-saving (auto-saving a half-typed translation is worse than losing it
    // — it publishes it to the app).
    const close = () => {
        if (dirty && !window.confirm('יש תרגומים שלא נשמרו. לסגור בכל זאת?')) {
            return;
        }
        setDirty(false);
        setOpen(false);
    };

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={() => setOpen(true)}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: t.white,
                    color: t.green,
                    height: 36,
                    px: '14px',
                    borderRadius: t.rs,
                    fontSize: '13px',
                    fontWeight: 600,
                    border: `1px solid ${t.green}55`,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'all .14s',
                    '&:hover': { background: t.greenLight },
                }}
            >
                <TranslateIcon sx={{ fontSize: 17 }} />
                {label}
            </Box>

            {/* Mounted only while open so the panel's fetch happens on demand, not on every form render. */}
            {open && (
                <MyDialog
                    isOpen={open}
                    close={close}
                    title={dialogTitle || label}
                    isTitleCentered={false}
                    showCloseButton
                    actions={null}
                >
                    <Box sx={{ pt: 1 }}>
                        <TranslationPanel
                            {...panelProps}
                            hideHeader
                            onDirtyChange={setDirty}
                            onSaved={onSaved}
                        />
                    </Box>
                </MyDialog>
            )}
        </>
    );
};

export default TranslationDialogButton;
